import { Grid2 } from '@mui/material';
import { useCallback, useContext } from 'react';
import ImageUploaderComponent from '../../../../components/ImageUploaderComponent';
import { AppContext } from 'context/AppContextProvider';
import { useTranslation } from 'react-i18next';

interface ISportHallAdvertisingEditionProps {
  advertisingImages: string[];
  onChange: (imageFiles: string[]) => void;
}

const SportHallAdvertisingEdition = ({
  advertisingImages = [],
  onChange,
}: ISportHallAdvertisingEditionProps) => {
  const { t } = useTranslation();
  const { showSnackbar } = useContext(AppContext);
  const MAX_FILES = 6;
  const MIN_DIMENSIONS = { width: 250, height: 250 };
  const MAX_DIMENSIONS = { width: 1000, height: 1000 };

  const isDisabled = advertisingImages
    ? advertisingImages.length >= MAX_FILES
    : false;

  const validateImageDimensions = useCallback(
    (
      file: File,
    ): Promise<{ isValid: boolean; file: File; message?: string }> => {
      return new Promise((resolve) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(file);

        const cleanup = () => URL.revokeObjectURL(objectUrl);

        image.onload = () => {
          const { width, height } = image;
          const isValid =
            width >= MIN_DIMENSIONS.width &&
            height >= MIN_DIMENSIONS.height &&
            width <= MAX_DIMENSIONS.width &&
            height <= MAX_DIMENSIONS.height;

          cleanup();

          if (isValid) {
            resolve({ isValid: true, file });
          } else {
            const message = t(
              'sportsPage.blocks.sportBlockModal.invalidImageDimensions',
              `A imagem "${file.name}" (${width}x${height}px) tem dimensões inválidas. O tamanho deve estar entre ${MIN_DIMENSIONS.width}x${MIN_DIMENSIONS.height}px e ${MAX_DIMENSIONS.width}x${MAX_DIMENSIONS.height}px.`,
            );
            console.error(message);
            showSnackbar(message, 'error');
            resolve({ isValid: false, file, message });
          }
        };

        image.onerror = () => {
          cleanup();
          const message = t(
            'sportsPage.blocks.sportBlockModal.unreadableFile',
            `Não foi possível ler o arquivo "${file.name}".`,
          );
          console.error(message);
          resolve({ isValid: false, file, message });
        };

        image.src = objectUrl;
      });
    },
    [],
  );

  const fileToBase64 = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }, []);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const validationPromises = acceptedFiles.map(validateImageDimensions);
      const validationResults = await Promise.all(validationPromises);

      const validFiles = validationResults
        .filter((result) => result.isValid)
        .map((result) => result.file);

      if (validFiles.length === 0) {
        return;
      }

      const conversionPromises = validFiles.map((file) => fileToBase64(file));
      const results = await Promise.allSettled(conversionPromises);
      const successfulBase64: string[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          successfulBase64.push(result.value);
        }
      });

      if (successfulBase64.length > 0) {
        onChange([...advertisingImages, ...successfulBase64]);
      }
    },
    [advertisingImages, onChange, validateImageDimensions, fileToBase64],
  );

  const handleRemove = useCallback(
    (idx: number) => {
      const updatedImages = advertisingImages.filter(
        (_, index) => index !== idx,
      );
      onChange(updatedImages);
    },
    [advertisingImages, onChange],
  );

  return (
    <Grid2 size={12}>
      <ImageUploaderComponent
        initialPreviewUrls={advertisingImages}
        onDrop={handleDrop}
        onRemove={handleRemove}
        maxFiles={MAX_FILES}
        isDisabled={isDisabled}
        acceptedFiles={{ 'image/png': [] }}
      />
    </Grid2>
  );
};

export default SportHallAdvertisingEdition;
